import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import React, { useCallback, useState } from 'react';
import { ScrollView, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import ChallengeStatusBadges from '@/components/ChallengeStatusBadges';
import { SccPressable } from '@/components/SccPressable';
import { color } from '@/constant/color';
import { cn } from '@/utils/cn';
import RightArrowIcon from '@/assets/icon/ic_angle_bracket_right.svg';
import useAppComponents from '@/hooks/useAppComponents';
import useNavigation from '@/navigation/useNavigation';
import { useCheckAuth } from '@/utils/checkAuth';
export default function ChallengeSection({ onPressUpcomingChallenge, }) {
    const navigation = useNavigation();
    const { api } = useAppComponents();
    const checkAuth = useCheckAuth();
    const [activeFilter, setActiveFilter] = useState('all');
    const [challenges, setChallenges] = useState([]);
    const fetchChallenges = useCallback(() => {
        api
            .listChallengesPost({
            statuses: activeFilter === 'all' ? undefined : [activeFilter],
        })
            .then(res => {
            setChallenges(res.data.items);
        });
    }, [api, activeFilter]);
    useFocusEffect(useCallback(() => {
        fetchChallenges();
    }, [fetchChallenges]));
    const handleChallengePress = (challenge) => {
        if (challenge.status === 'Upcoming') {
            onPressUpcomingChallenge(challenge);
        }
        if (challenge.status === 'InProgress' || challenge.status === 'Closed') {
            navigation.navigate('ChallengeDetail', { challengeId: challenge.id });
        }
    };
    return (_jsxs(View, { className: "bg-white", children: [_jsxs(View, { className: "px-[20px] pt-[24px] pb-[8px] gap-[8px]", children: [_jsx(Text, { className: "text-[24px] leading-[32px] font-pretendard-bold text-black", children: "\uACC4\uB2E8 \uCC4C\uB9B0\uC9C0" }), _jsx(Text, { className: "text-[14px] leading-[20px] tracking-[-0.28px] font-pretendard-regular text-gray-v2-60", children: "\uC9C4\uD589 \uC911\uC778 \uCC4C\uB9B0\uC9C0\uC5D0 \uCC38\uC5EC\uD574 \uBCF4\uC138\uC694!" })] }), _jsx(ScrollView, { horizontal: true, showsHorizontalScrollIndicator: false, children: _jsxs(View, { className: "flex-row items-center px-[20px] py-[8px] gap-[6px]", children: [_jsx(FilterChip, { elementName: "challenge_filter_all", label: "\uC804\uCCB4\uBCF4\uAE30", active: activeFilter === 'all', onPress: () => setActiveFilter('all') }), _jsx(FilterChip, { elementName: "challenge_filter_in_progress", label: "\uC9C4\uD589 \uC911", active: activeFilter === 'InProgress', onPress: () => setActiveFilter('InProgress') }), _jsx(FilterChip, { elementName: "challenge_filter_upcoming", label: "\uC624\uD508 \uC608\uC815", active: activeFilter === 'Upcoming', onPress: () => setActiveFilter('Upcoming') }), _jsx(FilterChip, { elementName: "challenge_filter_closed", label: "\uC885\uB8CC\uB41C \uCC4C\uB9B0\uC9C0", active: activeFilter === 'Closed', onPress: () => setActiveFilter('Closed') })] }) }), _jsx(View, { className: "px-[20px] pt-[4px] pb-[80px] gap-[12px]", children: challenges.map(challenge => (_jsx(ChallengeCard, { challenge: challenge, onPress: () => checkAuth(() => handleChallengePress(challenge)) }, challenge.id))) })] }));
}
const filterChipVariantClassName = {
    active: 'bg-brand-5 border-brand-50',
    inactive: 'bg-white border-gray-v2-20',
};
const filterChipTextVariantClassName = {
    active: 'font-pretendard-medium text-brand-50',
    inactive: 'font-pretendard-regular text-gray-v2-90',
};
function FilterChip({ elementName, label, active, onPress, }) {
    return (_jsx(SccPressable, { elementName: elementName, onPress: onPress, className: cn('px-[12px] py-[6px] rounded-full border', active
            ? filterChipVariantClassName.active
            : filterChipVariantClassName.inactive), children: _jsx(Text, { className: cn('text-[14px] leading-[20px] tracking-[-0.28px]', active
                ? filterChipTextVariantClassName.active
                : filterChipTextVariantClassName.inactive), children: label }) }));
}
function ChallengeCard({ challenge, onPress, }) {
    const date = stringifyChallengeDate(challenge);
    return (_jsxs(SccPressable, { elementName: "challenge_card", logParams: { challenge_id: challenge.id }, onPress: () => {
            onPress(challenge);
        }, className: "flex-row items-center gap-[12px] pl-[20px] pr-[16px] py-[16px] rounded-[8px] border border-gray-v2-20 bg-white", children: [_jsxs(View, { className: "flex-1 gap-[8px]", children: [_jsx(ChallengeStatusBadges, { status: [challenge.status], isMyChallenge: challenge.hasJoined }), _jsxs(View, { className: "gap-[2px]", children: [_jsx(Text, { className: "text-[20px] leading-[28px] tracking-[-0.4px] font-pretendard-semibold text-black", children: challenge.name }), date && (_jsx(Text, { className: "text-[14px] leading-[20px] tracking-[-0.28px] font-pretendard-regular text-gray-v2-50", children: date }))] })] }), _jsx(RightArrowIcon, { color: color.black, width: 24, height: 24 })] }));
}
function stringifyChallengeDate(challenge) {
    if (challenge.startsAt.value > Date.now()) {
        const d = new Date(challenge.startsAt.value);
        return `${d.getFullYear()}. ${d.getMonth() + 1}. ${d.getDate()}부터`;
    }
    if (!challenge.endsAt) {
        return null;
    }
    const d = new Date(challenge.endsAt.value);
    return `${d.getFullYear()}. ${d.getMonth() + 1}. ${d.getDate()}까지`;
}
